<script>
  // Экран контекста чата (этап 10): какой «мозг» отвечает в чате — облако или дача —
  // и живой статус дачи (heartbeat демона, комнаты, сводка дома через tool-RPC).
  // Открывается свайпом влево из чата или кнопкой в шапке; свайп вправо — назад.
  import { onDestroy, onMount } from 'svelte';
  import { api } from '../lib/api.js';
  import { chatsStore, classifyChatError, setChatBrain } from '../lib/stores.js';
  import { swipe } from '../lib/actions/swipe.js';
  import { dragTo, navigateWithSlide, pageEntrance, snapBack } from '../lib/page-transition.js';
  import { createArm } from '../lib/arm.js';
  import { TEXT } from '../lib/ui-text.js';
  import { renderMarkdown } from '../lib/markdown.js';
  import { formatStageRange, stageTitle } from '../lib/chat-stages.js';
  import PublicationPanel from '../lib/components/PublicationPanel.svelte';
  import { formatTime as formatPublishTime, stateImportedAt } from '../lib/publication.js';

  export let chatId;

  // ── Этапы проекта (этап 23): карточки «Этапы», «Состояние проекта», «Память проекта» ──
  const STAGE_TEMPLATES = ['project', 'research', 'trip', 'custom'];
  let stages = [];
  let projectState = null;
  // Этап 26.4: публикация на сайт — карточка только владельцу, когда она настроена.
  let publication = null;
  let articleTick = 0;
  let stageMemory = null;
  let stageTemplate = 'project';
  let stageTemplateCustom = '';
  let stagesLoaded = false;
  let stagesError = null;
  let templateSaving = false;
  let templateStatus = '';
  let templateError = null;
  let closingStage = false;
  let stageActionError = null;
  let stateEditing = false;
  let stateDraft = '';
  let stateBusy = false;
  let stateError = null;
  let stateNotice = '';
  let stateNoticeTimer = null;
  // В13 (1.27.8): «Очистить» и «Пересобрать с нуля» у сводки — вторым касанием,
  // и взведённая кнопка сама остывает (раньше «Очистить» оставалась взведённой навсегда).
  const { armed: stateArmed, tap: tapState } = createArm();
  let liveInfo = null;

  const STATUS_POLL_MS = 30000;
  const SENSORS_LIMIT = 12;

  let brain = 'cloud';
  let savingBrain = false;
  let brainError = null;
  let brainSuccess = false;
  let successTimer = null;

  let status = null;
  let statusLoading = true;
  let statusError = null;
  let now = Date.now();
  let pollTimer = null;

  let summary = null;
  let summaryLoading = false;
  let summaryError = null;

  $: chatMeta = $chatsStore.find((item) => item.chatId === chatId) || null;
  // Мозг переключает владелец (PUT settings — owner-only); участник видит текущий.
  $: isOwner = chatMeta ? chatMeta.isOwner !== false : true;
  $: currentBrain = chatMeta?.brain === 'dacha' ? 'dacha' : 'cloud';
  $: syncBrain(currentBrain);

  $: dachaEnabled = status ? status.enabled !== false : true;
  $: dachaOnline = Boolean(status?.online);
  $: rooms = Array.isArray(status?.rooms) ? status.rooms : [];
  $: statusText = statusLoading
    ? ''
    : (!dachaEnabled
        ? TEXT.dacha.notEnabled
        : `${dachaOnline ? TEXT.dacha.online : TEXT.dacha.offline} · ${lastSeenLabel(status?.lastSeen, now)}`);
  $: brainLabel = brain === 'dacha' ? TEXT.dacha.brainDacha : TEXT.dacha.brainCloud;
  $: sensors = Array.isArray(summary?.sensors) ? summary.sensors.slice(0, SENSORS_LIMIT) : [];

  $: isDachaChat = currentBrain === 'dacha' || chatMeta?.chatType === 'dacha';
  $: hasStages = stages.length > 0;
  $: stateTitle = stageTemplate === 'research'
    ? TEXT.stages.stateResearchTitle
    : (stageTemplate === 'trip' ? TEXT.stages.stateTripTitle : TEXT.stages.stateTitle);
  $: stateRendered = projectState?.summary ? renderMarkdown(projectState.summary) : '';
  $: pendingWork = projectState?.status === 'pending' || stages.some((item) => item.status === 'pending');
  $: memoryPercent = stageMemory && stageMemory.budgetChars
    ? Math.min(999, Math.round(((stageMemory.stateChars || 0) + (stageMemory.summaryChars || 0)) / stageMemory.budgetChars * 100))
    : 0;
  $: memoryChars = stageMemory ? (stageMemory.stateChars || 0) + (stageMemory.summaryChars || 0) : 0;
  $: memoryWarning = Boolean(stageMemory && ((stageMemory.trimmedStages || 0) > 0 || (stageMemory.droppedStages || 0) > 0));
  $: chatId && loadStages();

  async function loadStages() {
    stagesError = null;
    try {
      const data = await api.getStages(chatId);
      stages = Array.isArray(data?.stages) ? data.stages : [];
      projectState = data?.state ?? null;
      stageMemory = data?.memory ?? null;
      if (!templateSaving) {
        stageTemplate = STAGE_TEMPLATES.includes(data?.template?.preset) ? data.template.preset : 'project';
        stageTemplateCustom = data?.template?.custom || '';
      }
      stagesLoaded = true;
    } catch (e) {
      stagesError = classifyChatError(e, TEXT.stages.actionFailed);
      stagesLoaded = true;
    }
  }

  function templateLabel(preset) {
    return {
      project: TEXT.stages.templateProject,
      research: TEXT.stages.templateResearch,
      trip: TEXT.stages.templateTrip,
      custom: TEXT.stages.templateCustom,
    }[preset] || preset;
  }

  function stageStatusLabel(stage) {
    if (stage.status === 'pending') return TEXT.stages.pending;
    if (stage.status === 'failed') return TEXT.stages.failed.replace('{error}', stage.error || '');
    return TEXT.stages.cardMeta.replace('{count}', String(stage.messageCount ?? 0)).replace('{range}', formatStageRange(stage));
  }

  async function saveTemplate() {
    if (!isOwner) {
      return;
    }
    const custom = stageTemplateCustom.trim();
    if (stageTemplate === 'custom' && !custom) {
      templateError = TEXT.stages.templateCustomRequired;
      return;
    }
    templateSaving = true;
    templateError = null;
    templateStatus = '';
    try {
      await api.updateChatSettings(chatId, {
        stageTemplate,
        stageTemplateCustom: stageTemplate === 'custom' ? custom : (custom || null),
      });
      templateStatus = TEXT.stages.templateSaved;
      clearTimeout(successTimer);
      successTimer = setTimeout(() => {
        templateStatus = '';
      }, 3000);
    } catch (e) {
      templateError = e?.status === 422 ? TEXT.stages.templateCustomRequired : classifyChatError(e, TEXT.stages.templateFailed);
    } finally {
      templateSaving = false;
    }
  }

  function stageErrorText(e, fallback) {
    const detail = typeof e?.detail === 'string' ? e.detail : (e?.message || '');
    if (e?.status === 409) {
      if (/дачном/i.test(detail)) return TEXT.stages.closeDacha;
      if (/нечего/i.test(detail)) return TEXT.stages.closeNothing;
      if (/пишутся|пишется/i.test(detail)) return TEXT.stages.closePending;
      if (/generation/i.test(detail)) return TEXT.stages.closeBusy;
    }
    return classifyChatError(e, fallback);
  }

  async function closeStageHere() {
    closingStage = true;
    stageActionError = null;
    try {
      await api.closeStage(chatId, null);
      await loadStages();
    } catch (e) {
      stageActionError = stageErrorText(e, TEXT.stages.closeFailed);
    } finally {
      closingStage = false;
    }
  }

  // Этап 26.4: «Закрыть этап и опубликовать» — конспект допишется фоном и сам попадёт
  // на сайт (автопубликация новых этапов).
  async function closeStageFromContextAndPublish(options) {
    closingStage = true;
    stageActionError = null;
    try {
      await api.closeStage(chatId, null);
      await loadStages();
      publication = await api.publishChat(chatId, options);
    } catch (e) {
      stageActionError = stageErrorText(e, TEXT.stages.closeFailed);
    } finally {
      closingStage = false;
    }
  }

  function notice(text) {
    stateNotice = text;
    clearTimeout(stateNoticeTimer);
    stateNoticeTimer = setTimeout(() => {
      stateNotice = '';
    }, 3000);
  }

  function startStateEdit() {
    stateDraft = projectState?.summary || '';
    stateEditing = true;
    stateError = null;
  }

  async function saveState() {
    const text = stateDraft.trim();
    if (!text) {
      return;
    }
    stateBusy = true;
    stateError = null;
    try {
      projectState = await api.updateProjectState(chatId, text);
      stateEditing = false;
      notice(TEXT.stages.stateSaved);
    } catch (e) {
      stateError = stageErrorText(e, TEXT.stages.stateFailedSave);
    } finally {
      stateBusy = false;
    }
  }

  async function regenerateState(fromScratch) {
    stateBusy = true;
    stateError = null;
    try {
      const result = await api.regenerateProjectState(chatId, fromScratch);
      if (result) {
        projectState = result;
      }
    } catch (e) {
      stateError = stageErrorText(e, TEXT.stages.actionFailed);
    } finally {
      stateBusy = false;
    }
  }

  async function clearState() {
    if (!tapState('clear')) {
      return;
    }
    stateBusy = true;
    stateError = null;
    try {
      await api.clearProjectState(chatId);
      projectState = null;
    } catch (e) {
      stateError = stageErrorText(e, TEXT.stages.actionFailed);
    } finally {
      stateBusy = false;
    }
  }

  async function loadPublication() {
    if (!isOwner) {
      publication = null;
      return;
    }
    try {
      const data = await api.getPublication(chatId);
      publication = data?.available ? data : null;
    } catch {
      publication = null;
    }
  }

  function handleStageEvent(e) {
    const payload = e.detail;
    if (payload?.type === 'publication' && payload.chatId === chatId) {
      if (payload.article) {
        articleTick += 1;
      } else {
        loadPublication();
      }
      return;
    }
    if (!payload || payload.type !== 'stage' || payload.chatId !== chatId) {
      return;
    }
    if (payload.memory) {
      stageMemory = payload.memory;
    }
    if (payload.action === 'state' && 'state' in payload) {
      projectState = payload.state;
      return;
    }
    // Остальные действия меняют список этапов — перечитываем целиком (редко и дёшево).
    loadStages();
  }

  function syncBrain(value) {
    // Значение из /api/chats — источник правды, пока идёт сохранение не трогаем.
    if (!savingBrain) {
      brain = value;
    }
  }

  function lastSeenLabel(iso, nowMs) {
    if (!iso) {
      return TEXT.dacha.lastSeenNever;
    }
    const parsed = Date.parse(iso);
    if (Number.isNaN(parsed)) {
      return TEXT.dacha.lastSeenNever;
    }
    const minutes = Math.floor(Math.max(0, nowMs - parsed) / 60000);
    if (minutes < 1) {
      return TEXT.dacha.lastSeenNow;
    }
    return TEXT.dacha.lastSeen.replace('{minutes}', String(minutes));
  }

  function formatTime(iso) {
    const parsed = iso ? new Date(iso) : new Date();
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    return parsed.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  }

  async function handleBrainChange() {
    const next = brain;
    if (!isOwner || next === currentBrain) {
      return;
    }
    savingBrain = true;
    brainError = null;
    brainSuccess = false;
    try {
      await setChatBrain(chatId, next);
      brainSuccess = true;
      clearTimeout(successTimer);
      successTimer = setTimeout(() => {
        brainSuccess = false;
      }, 3000);
    } catch (e) {
      // 409 — дача выключена на сервере или в чате есть этапы (этап 23); остальное — словарная классификация.
      const detail = typeof e?.detail === 'string' ? e.detail : (e?.message || '');
      brainError = e?.status === 409
        ? (/раскройте/i.test(detail) ? TEXT.stages.contextBrainBlocked : TEXT.dacha.brainDisabled)
        : classifyChatError(e, TEXT.dacha.brainSaveFailed);
      brain = currentBrain;
    } finally {
      savingBrain = false;
    }
  }

  async function loadStatus() {
    try {
      status = await api.getDachaStatus();
      statusError = null;
    } catch (e) {
      statusError = classifyChatError(e, TEXT.dacha.statusFailed);
    } finally {
      statusLoading = false;
      now = Date.now();
    }
  }

  async function refreshSummary() {
    summaryLoading = true;
    summaryError = null;
    try {
      summary = await api.getDachaSummary();
    } catch (e) {
      summaryError = e?.status === 503 ? TEXT.dacha.summaryOffline : classifyChatError(e, TEXT.dacha.summaryFailed);
    } finally {
      summaryLoading = false;
    }
  }

  // Назад — свайпом вправо (экран идёт за пальцем, влево — «резина», назначения нет)
  // или кнопкой; в обоих случаях экран уезжает, чат въезжает слева.
  let pageEl = null;

  function goBack() {
    return navigateWithSlide(pageEl, 'right', `/chat/${chatId}`);
  }

  function onSwipeMove(event) {
    dragTo(pageEl, event.detail.dx);
  }

  function onSwipeEnd(event) {
    if (!event.detail.committed) {
      snapBack(pageEl);
    }
  }

  onMount(() => {
    loadStatus();
    loadPublication();
    window.addEventListener('mqtt:event', handleStageEvent);
    pollTimer = setInterval(() => {
      // В фоне не дёргаем сервер: вкладка невидима — статус обновится при возврате.
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }
      loadStatus();
    }, STATUS_POLL_MS);
  });

  onDestroy(() => {
    clearInterval(pollTimer);
    clearTimeout(successTimer);
    clearTimeout(stateNoticeTimer);
    if (typeof window !== 'undefined') {
      window.removeEventListener('mqtt:event', handleStageEvent);
    }
  });
</script>

<div
  class="context-page"
  bind:this={pageEl}
  use:pageEntrance
  use:swipe={{ directions: 'right' }}
  on:swipemove={onSwipeMove}
  on:swipeend={onSwipeEnd}
  on:swiperight={goBack}
>
  <header class="header">
    <button class="back-btn" on:click={goBack} aria-label={TEXT.dacha.backToChat}>←</button>
    <h1>{TEXT.dacha.contextTitle}</h1>
  </header>

  <div class="context-content">
    <section class="card">
      <h2>{TEXT.dacha.brainLabel}</h2>
      {#if isOwner}
        <fieldset class="brain-mode" disabled={savingBrain}>
          <legend class="sr-only">{TEXT.dacha.brainLabel}</legend>
          <label class="radio-label">
            <input type="radio" name="brain" value="cloud" bind:group={brain} on:change={handleBrainChange} />
            <span>{TEXT.dacha.brainCloud}</span>
          </label>
          <p class="hint">{TEXT.dacha.brainCloudHint}</p>
          <label class="radio-label">
            <input type="radio" name="brain" value="dacha" bind:group={brain} on:change={handleBrainChange} disabled={hasStages && brain !== 'dacha'} />
            <span>{TEXT.dacha.brainDacha}</span>
          </label>
          <p class="hint">{TEXT.dacha.brainDachaHint}</p>
          {#if hasStages && brain !== 'dacha'}
            <p class="hint" data-testid="brain-blocked-hint">{TEXT.stages.contextBrainBlocked}</p>
          {/if}
        </fieldset>
        {#if brain === 'dacha' && !statusLoading && dachaEnabled && !dachaOnline}
          <p class="warning-message">{TEXT.dacha.brainOfflineWarning}</p>
        {/if}
        {#if brainError}
          <div class="error-message" role="alert">{brainError}</div>
        {/if}
        {#if brainSuccess}
          <div class="success-message" role="status">{TEXT.dacha.brainSaved}</div>
        {/if}
      {:else}
        <p class="read-only" data-testid="brain-read-only">{TEXT.dacha.brainReadOnly.replace('{brain}', brainLabel)}</p>
      {/if}
    </section>

    {#if publication}
      <!-- Этап 26.4: публикация на сайт — та же панель, что в шторке из шапки чата. -->
      <section class="card" data-testid="publication-card">
        <h2>{TEXT.publish.title}</h2>
        <PublicationPanel
          {chatId}
          {publication}
          {stages}
          hasState={Boolean(projectState?.summary)}
          {articleTick}
          on:changed={(e) => (publication = e.detail.publication)}
          on:close-stage-and-publish={(e) => closeStageFromContextAndPublish(e.detail)}
        />
      </section>
    {/if}

    <!-- Этап 23: этапы проекта, сводка и память. В дачном чате этапов нет (реш. 16). -->
    <section class="card" data-testid="stages-card">
      <h2>{TEXT.stages.contextTitle}</h2>
      {#if isDachaChat}
        <p class="read-only" data-testid="stages-dacha">{TEXT.stages.contextDacha}</p>
      {:else}
        {#if stagesError}
          <p class="hint error-hint" role="alert">{stagesError}</p>
        {/if}
        {#if stagesLoaded && !stages.length}
          <p class="hint">{TEXT.stages.contextEmpty}</p>
        {/if}
        {#if stages.length}
          <ul class="stage-list">
            {#each stages as stage, index (stage.stageId)}
              <li class="stage-row" class:pending={stage.status === 'pending'} class:failed={stage.status === 'failed'}>
                <span class="stage-row-title">{stageTitle(stage, index + 1, TEXT.stages.cardTitle)}</span>
                <span class="stage-row-meta">{stageStatusLabel(stage)}</span>
              </li>
            {/each}
          </ul>
        {/if}
        {#if isOwner}
          <div class="card-actions">
            <button type="button" class="btn-primary" disabled={closingStage || pendingWork} on:click={closeStageHere}>
              {TEXT.stages.contextClose}
            </button>
          </div>
          {#if stageActionError}
            <p class="hint error-hint" role="alert">{stageActionError}</p>
          {/if}
          <div class="template-block">
            <label class="hint" for="stage-template">{TEXT.stages.templateLabel}</label>
            <select id="stage-template" bind:value={stageTemplate} disabled={templateSaving} on:change={() => { if (stageTemplate !== 'custom') saveTemplate(); }}>
              {#each STAGE_TEMPLATES as preset (preset)}
                <option value={preset}>{templateLabel(preset)}</option>
              {/each}
            </select>
            {#if stageTemplate === 'custom'}
              <textarea
                class="template-custom"
                rows="3"
                maxlength="2000"
                placeholder={TEXT.stages.templateCustomPlaceholder}
                bind:value={stageTemplateCustom}
                disabled={templateSaving}
              ></textarea>
              <div class="card-actions">
                <button type="button" class="btn-primary" disabled={templateSaving} on:click={saveTemplate}>{TEXT.stages.editSave}</button>
              </div>
            {/if}
            {#if templateStatus}
              <div class="success-message" role="status">{templateStatus}</div>
            {/if}
            {#if templateError}
              <div class="error-message" role="alert">{templateError}</div>
            {/if}
          </div>
        {:else if stages.length}
          <p class="hint">{TEXT.stages.templateLabel}: {templateLabel(stageTemplate)}</p>
        {/if}
      {/if}
    </section>

    {#if !isDachaChat && (projectState || stages.length)}
      <section class="card" data-testid="project-state-card">
        <h2>{stateTitle}</h2>
        {#if !projectState}
          <p class="hint">{TEXT.stages.stateEmpty}</p>
        {:else if projectState.hidden}
          <p class="read-only">{TEXT.stages.stateHidden}</p>
        {:else}
          {#if projectState.status === 'pending'}
            <p class="hint" role="status"><span class="spinner small" aria-hidden="true"></span> {TEXT.stages.statePending}</p>
          {:else if projectState.status === 'failed'}
            <p class="hint error-hint" role="alert">{TEXT.stages.stateFailed.replace('{error}', projectState.error || '')}</p>
          {/if}
          {#if stateEditing}
            <textarea class="state-editor" rows="10" bind:value={stateDraft} disabled={stateBusy}></textarea>
            <div class="card-actions">
              <button type="button" class="btn-primary" disabled={stateBusy || !stateDraft.trim()} on:click={saveState}>{TEXT.stages.stateSave}</button>
              <button type="button" class="btn-secondary" disabled={stateBusy} on:click={() => (stateEditing = false)}>{TEXT.stages.stateCancel}</button>
            </div>
          {:else if projectState.summary}
            <div class="state-text markdown-body" data-testid="project-state-text">{@html stateRendered}</div>
          {/if}
          {#if stateImportedAt(publication)}
            <p class="hint git-mark" data-testid="state-git-mark">{TEXT.publish.importedMark.replace('{time}', formatPublishTime(stateImportedAt(publication)))}</p>
          {/if}
        {/if}
        {#if isOwner && projectState && !projectState.hidden && !stateEditing}
          <div class="card-actions">
            <button type="button" class="btn-secondary" disabled={stateBusy || pendingWork} on:click={startStateEdit}>{TEXT.stages.stateEdit}</button>
            <button type="button" class="btn-secondary" disabled={stateBusy || pendingWork} on:click={() => regenerateState(false)}>{TEXT.stages.stateRegenerate}</button>
            <button type="button" class="btn-secondary" class:danger={$stateArmed === 'rebuild'} disabled={stateBusy || pendingWork} on:click={() => tapState('rebuild') && regenerateState(true)}>
              {$stateArmed === 'rebuild' ? TEXT.stages.stateRegenerateFromScratchConfirm : TEXT.stages.stateRegenerateFromScratch}
            </button>
            <button type="button" class="btn-secondary danger" disabled={stateBusy || pendingWork} on:click={clearState}>
              {$stateArmed === 'clear' ? TEXT.stages.stateClearConfirm : TEXT.stages.stateClear}
            </button>
          </div>
        {/if}
        {#if stateError}
          <p class="hint error-hint" role="alert">{stateError}</p>
        {/if}
        {#if stateNotice}
          <div class="success-message" role="status">{stateNotice}</div>
        {/if}
      </section>

      {#if stageMemory}
        <section class="card" data-testid="memory-card">
          <h2>{TEXT.stages.memoryTitle}</h2>
          <p class="hint">{TEXT.stages.memoryStages.replace('{count}', String(stageMemory.stages ?? stages.length))}</p>
          <p class="hint">
            {TEXT.stages.memoryChars
              .replace('{chars}', String(memoryChars))
              .replace('{tokens}', String(Math.round(memoryChars / 4)))
              .replace('{percent}', String(memoryPercent))}
          </p>
          {#if stageMemory.trimmedStages}
            <p class="hint">{TEXT.stages.memoryTrimmed.replace('{count}', String(stageMemory.trimmedStages))}</p>
          {/if}
          {#if stageMemory.droppedStages}
            <p class="hint">{TEXT.stages.memoryDropped.replace('{count}', String(stageMemory.droppedStages))}</p>
          {/if}
          {#if memoryWarning}
            <p class="warning-message" data-testid="memory-warning">{TEXT.stages.memoryWarning}</p>
          {/if}
        </section>
      {/if}
    {/if}

    <section class="card">
      <h2>{TEXT.dacha.cardTitle}</h2>
      <p class="dacha-status" data-testid="dacha-status" class:online={dachaOnline} class:offline={!dachaOnline && dachaEnabled}>
        {#if statusLoading}
          <span class="spinner small" aria-hidden="true"></span>
        {:else}
          {statusText}
        {/if}
      </p>
      {#if statusError}
        <p class="hint error-hint">{statusError}</p>
      {/if}
      {#if dachaEnabled && !statusLoading}
        {#if rooms.length}
          <p class="hint">{TEXT.dacha.rooms}</p>
          <div class="chips">
            {#each rooms as room (room)}
              <span class="chip">{room}</span>
            {/each}
          </div>
        {/if}
        {#if typeof status?.toolCount === 'number'}
          <p class="hint">{TEXT.dacha.tools.replace('{count}', String(status.toolCount))}</p>
        {/if}
        <div class="card-actions">
          <button type="button" class="btn-primary" disabled={summaryLoading || !dachaOnline} on:click={refreshSummary}>
            {summaryLoading ? TEXT.dacha.summaryLoading : TEXT.dacha.refreshSummary}
          </button>
        </div>
        {#if summaryError}
          <p class="hint error-hint" role="alert">{summaryError}</p>
        {/if}
        {#if summary && !summaryLoading}
          <div class="summary" data-testid="dacha-summary">
            {#if summary.alerts}
              <h3>{TEXT.dacha.summaryAlerts}</h3>
              <p class="summary-alerts">{summary.alerts}</p>
            {/if}
            {#if sensors.length}
              <h3>{TEXT.dacha.summarySensors}</h3>
              <table class="sensors">
                <tbody>
                  {#each sensors as sensor, index (sensor.name ?? index)}
                    <tr>
                      <td class="sensor-name">{sensor.name}{sensor.room ? ` · ${sensor.room}` : ''}</td>
                      <td class="sensor-value">{sensor.value}{sensor.units ? ` ${sensor.units}` : ''}</td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            {/if}
            <p class="hint">{TEXT.dacha.summaryUpdated.replace('{time}', formatTime(summary.ts))}</p>
          </div>
        {/if}
      {/if}
      <p class="hint">{TEXT.dacha.hint}</p>
    </section>
  </div>
</div>

<style>
  .context-page {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    /* Горизонталь — наш свайп, вертикаль — браузеру (скролл). */
    touch-action: pan-y;
  }

  .header {
    display: flex;
    align-items: center;
    gap: var(--spacing-md);
    padding: var(--spacing-md) var(--spacing-lg);
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-color);
  }

  .back-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    /* В11 (1.27.2): цель касания 44px (HIG); отрицательный margin сохраняет плотность шапки. */
    width: 44px;
    height: 44px;
    margin: -6px;
    background: none;
    border: none;
    color: var(--text-primary);
    font-size: 1.25rem;
    border-radius: 8px;
    cursor: pointer;
  }

  .back-btn:hover {
    background: var(--bg-tertiary);
  }

  .header h1 {
    font-size: 1rem;
    font-weight: 600;
  }

  .context-content {
    flex: 1;
    overflow-y: auto;
    padding: var(--spacing-lg);
    overscroll-behavior: contain;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-lg);
    max-width: 600px;
    width: 100%;
    margin: 0 auto;
    box-sizing: border-box;
  }

  .stage-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .stage-row {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    padding: 0.4rem 0.6rem;
    border-radius: 8px;
    background: var(--bg-tertiary);
  }

  .stage-row.pending {
    border: 1px dashed var(--border-color);
  }

  .stage-row.failed {
    border: 1px solid var(--error-color, #d9534f);
  }

  .stage-row-title {
    font-weight: 600;
    font-size: 0.9rem;
  }

  .stage-row-meta {
    font-size: 0.75rem;
    color: var(--text-secondary);
  }

  .template-block {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .template-block select,
  .template-custom,
  .state-editor {
    width: 100%;
    box-sizing: border-box;
    font-size: 16px;
    padding: 0.5rem;
    border-radius: 8px;
    border: 1px solid var(--border-color);
    background: var(--bg-primary);
    color: var(--text-primary);
  }

  .state-text {
    font-size: 0.95rem;
    line-height: 1.45;
  }

  .state-text :global(h2),
  .state-text :global(h3) {
    font-size: 0.95rem;
    margin: 0.6rem 0 0.2rem;
  }

  .btn-secondary {
    padding: 0.45rem 0.8rem;
    border-radius: 999px;
    border: 1px solid var(--accent-color);
    background: var(--bg-primary);
    color: var(--accent-color);
    font-size: 0.85rem;
    cursor: pointer;
  }

  .btn-secondary.danger {
    border-color: var(--error-color, #d9534f);
    color: var(--error-color, #d9534f);
  }

  .card-actions {
    flex-wrap: wrap;
  }

  .card {
    padding: var(--spacing-md);
    border-radius: 12px;
    background: var(--bg-secondary);
    border: 1px solid var(--border-light);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  .card h2 {
    font-size: 0.95rem;
    font-weight: 600;
    margin: 0;
  }

  .card h3 {
    font-size: 0.85rem;
    font-weight: 600;
    margin: var(--spacing-xs) 0 0;
    color: var(--text-secondary);
  }

  .brain-mode {
    border: none;
    padding: 0;
    margin: 0;
    min-width: 0;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  .radio-label {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-height: 44px;
    font-weight: 500;
    color: var(--text-primary);
    cursor: pointer;
  }

  .radio-label input {
    width: 20px;
    height: 20px;
    margin: 0;
    accent-color: var(--accent-color);
  }

  .git-mark {
    color: var(--accent-color);
  }

  .hint {
    margin: 0;
    font-size: 0.75rem;
    color: var(--text-muted);
  }

  .error-hint {
    color: var(--error-text);
  }

  .read-only,
  .dacha-status {
    margin: 0;
    color: var(--text-secondary);
  }

  .dacha-status.online {
    color: var(--success-color);
  }

  .dacha-status.offline {
    color: var(--warning-text);
  }

  .warning-message {
    margin: 0;
    padding: 0.75rem;
    background: var(--warning-bg);
    color: var(--warning-text);
    border-radius: 8px;
    font-size: 0.85rem;
  }

  .error-message {
    padding: 0.75rem;
    background: var(--error-bg);
    color: var(--error-text);
    border-radius: 8px;
  }

  .success-message {
    padding: 0.75rem;
    background: rgba(16, 185, 129, 0.1);
    color: var(--success-color);
    border-radius: 8px;
  }

  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .chip {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.625rem;
    border-radius: 999px;
    font-size: 0.8rem;
    background: var(--bg-tertiary);
    color: var(--text-secondary);
  }

  .card-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .btn-primary {
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    background: var(--accent-color);
    color: white;
    transition: all var(--transition-fast);
  }

  .btn-primary:hover:not(:disabled) {
    background: var(--accent-hover);
  }

  .btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .summary {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
  }

  .summary-alerts {
    margin: 0;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .sensors {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
  }

  .sensors td {
    padding: 0.375rem 0;
    border-bottom: 1px solid var(--border-light);
    vertical-align: top;
  }

  .sensor-name {
    color: var(--text-secondary);
    overflow-wrap: anywhere;
  }

  .sensor-value {
    text-align: right;
    white-space: nowrap;
    padding-left: var(--spacing-sm);
  }

  .spinner {
    display: inline-block;
    width: 32px;
    height: 32px;
    border: 3px solid var(--border-color);
    border-top-color: var(--accent-color);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  .spinner.small {
    width: 16px;
    height: 16px;
    border-width: 2px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
</style>
